import { TimeRangeFormat } from 'app/models/filter-input';
import { TaskLegend } from 'app/models/api/color';
import { DateRange } from 'app/models/date-range';
import { ArrayUtils, ObjectUtils } from '@festo-ax/smartenance-shared-ui';
import { DateRangeEnum, DateUtils } from './date';
import { MachineBriefData } from '../models/api/machines';
import { TagObjectInterface } from 'app/models/tag';

export class ComparatorUtils {
  static compareTimeRangeFormat(tr1: TimeRangeFormat, tr2: TimeRangeFormat): boolean {
    if (ObjectUtils.isNotObject(tr1) || ObjectUtils.isNotObject(tr2)) {
      return tr1 === tr2;
    }

    return tr1.from === tr2.from && tr1.to === tr2.to;
  }

  static compareTagObjectInterfaces(obj1: TagObjectInterface | null, obj2: TagObjectInterface | null): boolean {
    if (ObjectUtils.isNotObject(obj1) || ObjectUtils.isNotObject(obj2)) {
      return obj1 === obj2;
    }

    return obj1.id === obj2.id && obj1.name === obj2.name;
  }

  static compareTagObjectInterfacesFormatArrays(arr1: TagObjectInterface[] | null, arr2: TagObjectInterface[] | null): boolean {
    const isArr1Empty: boolean = ArrayUtils.isEmpty(arr1);
    const isArr2Empty: boolean = ArrayUtils.isEmpty(arr2);

    if (isArr1Empty && isArr2Empty) {
      return true; // either empty or null does not matter no changes needed
    }
    if (isArr1Empty || isArr2Empty) {
      return false; // but if only one empty there were changes
    }
    if (arr1.length !== arr2.length) {
      return false;
    }

    const sortedArr1 = [...arr1].sort((a, b) => a.id.localeCompare(b.id));
    const sortedArr2 = [...arr2].sort((a, b) => a.id.localeCompare(b.id));

    for (let i = 0; i < arr1.length; i++) {
      if (!ComparatorUtils.compareTagObjectInterfaces(sortedArr1[i], sortedArr2[i])) {
        return false; // If any element at the same index differs, the arrays are not equal
      }
    }

    return true;
  }

  static compareTaskLegendArray(arr1: TaskLegend[], arr2: TaskLegend[]): boolean {
    const isArr1Empty: boolean = ArrayUtils.isEmpty(arr1);
    const isArr2Empty: boolean = ArrayUtils.isEmpty(arr2);

    if (isArr1Empty && isArr2Empty) {
      return true; // either empty or null does not matter no changes needed
    }
    if (isArr1Empty || isArr2Empty) {
      return false; // but if only one empty there were changes
    }
    if (arr1.length !== arr2.length) {
      return false;
    }

    const arr1Ids: string[] = arr1.map((tl: TaskLegend) => tl.id);
    const arr2Ids: string[] = arr2.map((tl: TaskLegend) => tl.id);

    return ArrayUtils.areStringArraysEqual(arr1Ids, arr2Ids);
  }

  static compareDateRange(obj1: DateRange | DateRangeEnum, obj2: DateRange | DateRangeEnum): boolean {
    if (!DateUtils.isValueDateRange(obj1) || !DateUtils.isValueDateRange(obj2)) {
      return obj1 === obj2;
    }

    const obj1From: Date = DateUtils.isDate(obj1.from) ? obj1.from : new Date(obj1.from);
    const obj2From: Date = DateUtils.isDate(obj2.from) ? obj2.from : new Date(obj2.from);
    const obj1To: Date = DateUtils.isDate(obj1.to) ? obj1.to : new Date(obj1.to);
    const obj2To: Date = DateUtils.isDate(obj2.to) ? obj2.to : new Date(obj2.to);

    return obj1.disabled === obj2.disabled
      && obj1From.getTime() === obj2From.getTime()
      && obj1To.getTime() === obj2To.getTime();
  }

  static compareMachineBriefData(m1: MachineBriefData[] | null, m2: MachineBriefData[] | null): boolean {
    if (ObjectUtils.isNotObject(m1) || ObjectUtils.isNotObject(m2)) {
      return m1 === m2;
    }

    const m1Ids: string[] = m1.map(m => m.id);
    const m2Ids: string[] = m2.map(m => m.id);

    return ArrayUtils.areStringArraysEqual(m1Ids, m2Ids);
  }

  static areUrlsEqual(url1: string, url2: string, skipValues?: string[]): boolean {
    if (typeof url1 !== 'string' || typeof url2 !== 'string') {
      return false;
    }

    const arr1: string[] = url1.split('/');
    const arr2: string[] = url2.split('/');

    if (arr1.length !== arr2.length) {
      return false; // If arrays have different lengths, they can't be equal
    }

    for (let i = 0; i < arr1.length; i++) {
      if (skipValues && (skipValues.includes(arr1[i]) || skipValues.includes(arr2[i]))) {
        continue; // Skip the comparison if the value is in skipValues array
      }

      if (arr1[i] !== arr2[i]) {
        return false; // If any element at the same index differs, the arrays are not equal
      }
    }

    return true; // All elements are equal
  }
}
